import React, { useState, useContext } from "react";
import { Menu, Row, Button, Drawer, Switch, Space } from "antd";
import {
  FileTextOutlined,
  MenuOutlined,
  CloseOutlined,
  CaretDownOutlined,
  UploadOutlined,
  TeamOutlined,
  CheckCircleOutlined,
  SettingOutlined,
  ToolOutlined,
  BankOutlined,
  ApartmentOutlined,
  EnvironmentOutlined,
  IdcardOutlined,
  CommentOutlined,
  MessageOutlined,
  FontColorsOutlined,
  BgColorsOutlined,
  NotificationOutlined,
  RocketOutlined,
  BarChartOutlined,
} from "@ant-design/icons";
import RouterSwitch from "./common/routerSwitch";
import DensityPicker from "./densityPicker";
import { useNavigate } from "react-router";
import "./navbarpage.css";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { loginRequest } from "../authConfig";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { AppContext } from "../AppContextProvider";
import useMeasure from "react-use-measure";
import { IoSunnySharp, IoMoonSharp } from "react-icons/io5";
import usePermissions from "../hooks/usePermissions";
import useIsAdmin from "../hooks/useIsAdmin";
import useHasReports from "../hooks/useHasReports";
import DevRoleSwitcher from "./DevRoleSwitcher";
import NotificationBell from "./NotificationBell";
import FeedbackFormModal from "./feedbackFormModal";
import AnnouncementBanner from "./announcementBanner";
import WhatsNewModal from "./whatsNewModal";
import { clearDrills } from "../utils/drillHandoff";

const NavBarPage = () => {
  const { instance } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [visible, setVisible] = useState(false);
  // The bug / feature request form. It lives in the user menu rather than the
  // main nav because it is about the APP, not about deals — same shelf as
  // Theme and Sign Out — and it opens over whatever page the user is on, so
  // they don't lose the screen they were about to report.
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  // "What's new" opened deliberately from the user menu, which shows the FULL
  // release history. The unprompted "the app updated since you were last
  // here" popup is the modal's own business -- see whatsNewModal.jsx.
  const [whatsNewOpen, setWhatsNewOpen] = useState(false);
  const { setUser, user, theme, toggleTheme } = useContext(AppContext);
  const { can } = usePermissions();
  const { isAdmin } = useIsAdmin();
  const navigate = useNavigate();
  const [ref] = useMeasure();

  const showDrawer = () => {
    setVisible(true);
  };

  const onClose = () => {
    setVisible(false);
  };

  const handleDealsClick = () => {
    navigate("/");
    setVisible(false);
  };

  const handleBrokersClick = () => {
    navigate("/brokers");
    setVisible(false);
  };

  const handleUploadClick = () => {
    navigate("/upload");
    setVisible(false);
  };

  const handleApprovalsClick = () => {
    navigate("/approvals");
    setVisible(false);
  };

  const handleReportsClick = () => {
    navigate("/reports");
    setVisible(false);
  };

  const handlePickListsClick = () => {
    navigate("/admin/pick-lists");
    setVisible(false);
  };

  const handleEntitiesClick = () => {
    navigate("/admin/entities");
    setVisible(false);
  };

  const handleOperatorsClick = () => {
    navigate("/admin/operators");
    setVisible(false);
  };

  const handleAoisClick = () => {
    navigate("/admin/aois");
    setVisible(false);
  };

  const handleLandmenClick = () => {
    navigate("/admin/landmen");
    setVisible(false);
  };

  const handleFeedbackListClick = () => {
    navigate("/feedback");
    setVisible(false);
  };

  const handleAnnouncementsClick = () => {
    navigate("/admin/announcements");
    setVisible(false);
  };

  const handleFieldSettingsClick = () => {
    navigate("/admin/metadata");
    setVisible(false);
  };

  const handleAppearanceClick = () => {
    navigate("/admin/appearance");
    setVisible(false);
  };

  const openFeedbackForm = () => {
    setFeedbackOpen(true);
    setVisible(false);
  };

  const openWhatsNew = () => {
    setWhatsNewOpen(true);
    setVisible(false);
  };

  // Approval queue nav is visible only to roles that can view it (Capex
  // approvers, plus superusers / role-less transition users via can()).
  const showApprovals = can("approvalQueue.view");

  // Upload (flexible broker-file import) is gated to deal.create — broker
  // agents / landmen (plus superusers / role-less transition users via can()).
  const showUpload = can("deal.create");

  // Brokers (the brokerage roster) is gated to brokers.view — the internal
  // roles. A BD.BrokerAgent has no business browsing the other brokerages, so
  // the tab is hidden and /brokers self-redirects for them.
  const showBrokers = can("brokers.view");

  // Reports (#239) are for every BD role: the server scopes what each widget
  // counts, so a broker agent sees reports over their own deals only. Authors
  // always get the menu (to create reports); everyone else only once they
  // have at least one report to open, not a menu to an empty list.
  const canManageReports = can("reports.manage");
  const hasReports = useHasReports(can("reports.view") && !canManageReports);
  const showReports = can("reports.view") && (canManageReports || hasReports);

  // Admin → Pick Lists is gated to admin.pickLists — the BD.Admin role
  // (plus superusers). Note: role-less transition users also pass can(), but
  // the /pick_lists API is the real boundary and 403s non-admins.
  const showPickLists = can("admin.pickLists");

  // Admin → Entities is gated to admin.entities — the BD.Admin role (plus
  // superusers). Role-less transition users also pass can(), but the
  // /entities update API is the real boundary and 403s non-admins.
  const showEntities = can("admin.entities");

  // Admin → Operators is gated to admin.operators — the BD.Admin role (plus
  // superusers). Role-less transition users also pass can(), but the
  // /operators update API is the real boundary and 403s non-admins.
  const showOperators = can("admin.operators");

  // Admin → AOIs is gated to admin.aois — the BD.Admin role (plus superusers).
  // Role-less transition users also pass can(), but the /aois write APIs are
  // the real boundary and 403 non-admins.
  const showAois = can("admin.aois");

  // Admin → Landmen is gated to admin.landmen — the BD.Admin role (plus
  // superusers). Note BD.Landman does NOT open this: holding the job role is
  // not the same as maintaining the roster. The /landmen write APIs are the
  // real boundary and 403 non-admins.
  const showLandmen = can("admin.landmen");

  // Admin → Feedback and Admin → Announcements are gated by the shared
  // App Support admin roles (VITE_ADMIN_ROLE — BD.Admin here — plus the
  // de/admin superusers), NOT by the local permissions map: both features
  // now come from de-hub’s shared `app_support` module, which decides
  // admin-ness per app from its own config. A second, local answer to the
  // same question is how the two drift apart. See src/config/appSupport.js.
  //
  // Feedback: this only gates the QUEUE tab. /feedback itself is open to
  // everyone, and every user reaches their own tickets through "My
  // requests" in the user menu. Filing a report is never gated at all.
  const showFeedbackQueue = isAdmin;

  // Announcements: only AUTHORING is gated — everyone READS announcements
  // via the banner, which needs no nav entry at all.
  const showAnnouncements = isAdmin;

  const userLogout = async () => {
    // Report drill handoffs hold deal filters; none outlive the session.
    clearDrills();
    await instance.logout();
    setUser(null);
    localStorage.removeItem("currentUser");
  };

  // The configuration screens collapse into one nav submenu. Ten
  // top-level entries had filled the bar to its width; these are visited
  // occasionally to maintain vocabularies and reference data rather than on
  // the daily path, so they cost the most room and earn the least.
  //
  // Approvals and Feedback stay top-level despite also being admin-gated:
  // they are work queues someone checks daily, not configuration.
  //
  // Each child keeps its own permission gate. The parent is built from
  // whatever survives, and is only added to the bar when that list is
  // non-empty -- an "Admin" menu that opens onto nothing is worse than no
  // menu at all.
  const adminMenuChildren = [
    ...(showPickLists
      ? [
          {
            key: "pick-lists",
            label: "Pick Lists",
            icon: <SettingOutlined />,
            onClick: handlePickListsClick,
          },
        ]
      : []),
    ...(showEntities
      ? [
          {
            key: "entities",
            label: "Entities",
            icon: <BankOutlined />,
            onClick: handleEntitiesClick,
          },
        ]
      : []),
    ...(showOperators
      ? [
          {
            key: "operators",
            label: "Operators",
            icon: <ApartmentOutlined />,
            onClick: handleOperatorsClick,
          },
        ]
      : []),
    ...(showAois
      ? [
          {
            key: "aois",
            label: "AOIs",
            icon: <EnvironmentOutlined />,
            onClick: handleAoisClick,
          },
        ]
      : []),
    ...(showLandmen
      ? [
          {
            key: "landmen",
            label: "Landmen",
            icon: <IdcardOutlined />,
            onClick: handleLandmenClick,
          },
        ]
      : []),
    // Announcements, Field Settings and Appearance share one gate: the App
    // Support admin role (BD.Admin), and the server refuses everyone else
    // regardless.
    ...(showAnnouncements
      ? [
          {
            key: "announcements",
            label: "Announcements",
            icon: <NotificationOutlined />,
            onClick: handleAnnouncementsClick,
          },
          {
            key: "field-settings",
            label: "Field Settings",
            icon: <FontColorsOutlined />,
            onClick: handleFieldSettingsClick,
          },
          {
            key: "appearance",
            label: "Appearance",
            icon: <BgColorsOutlined />,
            onClick: handleAppearanceClick,
          },
        ]
      : []),
  ];

  return (
    <React.Fragment>
      <Row justify="space-between" align="middle" className="mobileMenu">
        <div style={{ marginBottom: 10 }} className="navbar-img-item"></div>
        <div className="app-brand app-brand--mobile">
          <img className="app-brand-mark" alt="" src="/deal-capture.svg" />
          <span className="app-brand-name">Deal Capture</span>
        </div>
        <Button
          className="menubtn"
          type="primary"
          shape="circle"
          icon={<MenuOutlined />}
          onClick={showDrawer}
        ></Button>
        <Drawer
          width={200}
          size="default"
          icon={<CloseOutlined />}
          justifyContent="start"
          placement="right"
          onClose={onClose}
          open={visible}
        >
          <div
            style={{
              justifyContent: "left",
              display: "flex",
              flexDirection: "column",
              paddingTop: 50,
            }}
          >
            <Button
              type="text"
              onClick={handleDealsClick}
              icon={<FileTextOutlined />}
            >
              Deals
            </Button>
            {showBrokers && (
              <Button
                type="text"
                onClick={handleBrokersClick}
                icon={<TeamOutlined />}
              >
                Brokers
              </Button>
            )}
            {showUpload && (
              <Button
                type="text"
                onClick={handleUploadClick}
                icon={<UploadOutlined />}
              >
                Upload
              </Button>
            )}
            {showApprovals && (
              <Button
                type="text"
                onClick={handleApprovalsClick}
                icon={<CheckCircleOutlined />}
              >
                Approvals
              </Button>
            )}
            {showReports && (
              <Button
                type="text"
                onClick={handleReportsClick}
                icon={<BarChartOutlined />}
              >
                Reports
              </Button>
            )}
            {showPickLists && (
              <Button
                type="text"
                onClick={handlePickListsClick}
                icon={<SettingOutlined />}
              >
                Pick Lists
              </Button>
            )}
            {showEntities && (
              <Button
                type="text"
                onClick={handleEntitiesClick}
                icon={<BankOutlined />}
              >
                Entities
              </Button>
            )}
            {showOperators && (
              <Button
                type="text"
                onClick={handleOperatorsClick}
                icon={<ApartmentOutlined />}
              >
                Operators
              </Button>
            )}
            {showAois && (
              <Button
                type="text"
                onClick={handleAoisClick}
                icon={<EnvironmentOutlined />}
              >
                AOIs
              </Button>
            )}
            {showLandmen && (
              <Button
                type="text"
                onClick={handleLandmenClick}
                icon={<IdcardOutlined />}
              >
                Landmen
              </Button>
            )}
            {showFeedbackQueue && (
              <Button
                type="text"
                onClick={handleFeedbackListClick}
                icon={<CommentOutlined />}
              >
                Feedback
              </Button>
            )}
            {showAnnouncements && (
              <Button
                type="text"
                onClick={handleAnnouncementsClick}
                icon={<NotificationOutlined />}
              >
                Announcements
              </Button>
            )}
            {isAuthenticated && (
              <Button
                type="text"
                onClick={openFeedbackForm}
                icon={<MessageOutlined />}
              >
                Send feedback
              </Button>
            )}
            {isAuthenticated && (
              <Button
                type="text"
                onClick={openWhatsNew}
                icon={<RocketOutlined />}
              >
                What&apos;s new
              </Button>
            )}
            {/* "My requests" for the non-admins, who don't get the gated
                Feedback tab above. Without it a reporter on a phone could
                file a ticket but had no way back to read the reply short of
                the notification bell. Admins already have the tab, so this
                would be a duplicate row for them. */}
            {isAuthenticated && !showFeedbackQueue && (
              <Button
                type="text"
                onClick={handleFeedbackListClick}
                icon={<CommentOutlined />}
              >
                My requests
              </Button>
            )}
            {isAuthenticated && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  paddingTop: 8,
                }}
              >
                <NotificationBell />
                <span>Notifications</span>
              </div>
            )}
            <Space
              style={{ alignSelf: "flex-end", paddingRight: 45 }}
              direction="Horizontal"
            >
             <Switch
                  style={{ backgroundColor: "#1668dc" }}
                  size="small"
                  checkedChildren={<IoSunnySharp color="yellow" />}
                  unCheckedChildren={<IoMoonSharp color="lightgrey" />}
                  checked={theme === "light"} // Use checked instead of defaultChecked for controlled behavior
                  onChange={toggleTheme}
                />
              <span>Theme </span>
            </Space>
            <div style={{ alignSelf: "flex-end", paddingRight: 45 }}>
              <DensityPicker />
            </div>
          </div>
        </Drawer>
      </Row>
      <></>
      <div ref={ref} style={{ display: "flex", alignItems: "center" }} className="bigmenu">
        <div style={{ marginBottom: 5 }} className="navbar-img-item "></div>
        {/* App lockup: the "Deal Capture" mark + wordmark, leading the nav bar.
            The corporate WIG logo is deliberately not shown here — this app
            carries its own branding. The wordmark is HTML text rather than
            baked into the SVG so it picks up the app font and stays
            selectable/announced by screen readers. */}
        <div className="app-brand">
          <img className="app-brand-mark" alt="" src="/deal-capture.svg" />
          <span className="app-brand-name">Deal Capture</span>
        </div>
        <Menu
          className="bigmenu"
          mode="horizontal"
          // Submenus open on CLICK, not hover. Hover does not exist on touch,
          // and a hover-opened nav menu is easy to trip over on the way to
          // something else.
          triggerSubMenuAction="click"
          defaultSelectedKeys={["2"]}
          style={{ flex: 1 }}
          items={[
            {
              key: "deals",
              label: "Deals",
              icon: <FileTextOutlined />,
              onClick: handleDealsClick,
            },
            ...(showBrokers
              ? [
                  {
                    key: "brokers",
                    label: "Brokers",
                    icon: <TeamOutlined />,
                    onClick: handleBrokersClick,
                  },
                ]
              : []),
            ...(showUpload
              ? [
                  {
                    key: "upload",
                    label: "Upload",
                    icon: <UploadOutlined />,
                    onClick: handleUploadClick,
                  },
                ]
              : []),
            ...(showApprovals
              ? [
                  {
                    key: "approvals",
                    label: "Approvals",
                    icon: <CheckCircleOutlined />,
                    onClick: handleApprovalsClick,
                  },
                ]
              : []),
            ...(showReports
              ? [
                  {
                    key: "reports",
                    label: "Reports",
                    icon: <BarChartOutlined />,
                    onClick: handleReportsClick,
                  },
                ]
              : []),
            ...(showFeedbackQueue
              ? [
                  {
                    key: "feedback",
                    label: "Feedback",
                    icon: <CommentOutlined />,
                    onClick: handleFeedbackListClick,
                  },
                ]
              : []),
            ...(adminMenuChildren.length
              ? [
                  {
                    key: "admin",
                    label: "Admin",
                    icon: <ToolOutlined />,
                    children: adminMenuChildren,
                  },
                ]
              : []),
            // DEV-only role switcher lives in the nav bar. Rendered as a
            // disabled menu entry whose label is the control itself, so it
            // sits inline with the menu without being a clickable nav item.
            // DevRoleSwitcher returns null in production builds.
            ...(import.meta.env.DEV
              ? [
                  {
                    key: "dev-roles",
                    style: { marginLeft: "auto", cursor: "default" },
                    disabled: true,
                    label: <DevRoleSwitcher />,
                  },
                ]
              : []),
            // Notification bell — any authenticated user. Rendered as a
            // disabled (non-navigable) menu entry whose label is the bell
            // control itself. In production (no DevRoleSwitcher) the bell
            // carries the marginLeft:auto that pushes the trailing items right.
            ...(isAuthenticated
              ? [
                  {
                    key: "notifications",
                    disabled: true,
                    style: {
                      cursor: "default",
                      ...(import.meta.env.DEV ? {} : { marginLeft: "auto" }),
                    },
                    label: <NotificationBell />,
                  },
                ]
              : []),
            {
              key: "user-menu",
              style: isAuthenticated ? {} : { marginLeft: "auto" },
              label: (
                <div>
                  {isAuthenticated ? (
                    <>
                      <FontAwesomeIcon icon="fa-solid fa-user" color="#ffffff" />
                      <CaretDownOutlined style={{ color: "#ffffff" }} />{" "}
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon="fa-solid fa-user-slash" color="#ffffff" />
                      <CaretDownOutlined style={{ color: "#ffffff" }} />
                    </>
                  )}
                </div>
              ),
              children: isAuthenticated
                ? [
                    {
                      key: "theme",
                      label: (
                        <div style={{ display: "flex", alignItems: "center", cursor: "default" }}>
                          <span style={{ marginRight: 8, opacity: 1 }}>Theme </span>
                          <Switch
                            style={{ backgroundColor: "#1668dc" }}
                            size="small"
                            checkedChildren={<IoSunnySharp color="yellow" />}
                            unCheckedChildren={<IoMoonSharp color="lightgrey" />}
                            checked={theme === "light"}
                            onChange={toggleTheme}
                          />
                        </div>
                      ),
                    },
                    // Personal density, over the admin's default. Next to
                    // Theme because both are "how the app looks to me".
                    {
                      key: "density",
                      label: <DensityPicker />,
                    },
                    // Report a bug / ask for a feature. Sits here — the
                    // app-level shelf next to Theme and Sign Out — because it
                    // is about the app rather than about deals, and because
                    // it must be reachable from whatever page just broke.
                    {
                      key: "feedback-new",
                      label: "Send feedback",
                      icon: <MessageOutlined />,
                      onClick: openFeedbackForm,
                    },
                    {
                      key: "feedback-mine",
                      label: "My requests",
                      icon: <CommentOutlined />,
                      onClick: handleFeedbackListClick,
                    },
                    // The release notes, on demand. Someone who dismissed the
                    // popup -- or joined after a release -- needs a way back
                    // to "what changed?" that isn't asking a colleague.
                    {
                      key: "whats-new",
                      label: "What's new",
                      icon: <RocketOutlined />,
                      onClick: openWhatsNew,
                    },
                    { type: "divider", key: "user-menu-divider" },
                    ...(user
                      ? [{ key: "6", label: user.name, onClick: () => userLogout() }]
                      : []),
                    { key: "7", label: "Sign Out", onClick: () => userLogout() },
                  ]
                : [
                    {
                      key: "5",
                      label: "Sign In",
                      onClick: () => instance.loginRedirect(loginRequest),
                    },
                  ],
            },
          ]}
        />
      </div>
      {/* Mounted once at the nav bar so the form is reachable from every
          route without each page having to host it. Renders nothing until
          opened. */}
      {isAuthenticated && (
        <FeedbackFormModal
          open={feedbackOpen}
          onClose={() => setFeedbackOpen(false)}
        />
      )}
      {/* Planned-outage banner (and, rarely, its modal). Mounted here rather
          than per-page so it sits above every route and survives navigation —
          the banner's whole job is to be on screen while someone works, not
          to greet them on one particular page. */}
      {isAuthenticated && <AnnouncementBanner />}
      {/* Release notes. Mounted here, not per-page, because it opens itself
          once per new build and must do so wherever the user landed. It
          renders nothing when there is nothing unseen and nobody asked. */}
      {isAuthenticated && (
        <WhatsNewModal
          open={whatsNewOpen}
          onClose={() => setWhatsNewOpen(false)}
        />
      )}
      <RouterSwitch />
    </React.Fragment>
  );
};

export default NavBarPage;
